import { useState } from 'react';

import { AgentPortalFeatureCode } from '@wallet-manager/pfh-pmp-node-def-types';

import APIs from '../../../api';
import { ApiResult } from '../../../api/types';
import Table from '../../../common/filterTable/components/Table';
import FilterContainer from '../../../common/filterTable/layout/FilterContainer';
import FilterTableLayoutContainer from '../../../common/filterTable/layout/FilterTableLayoutContainer';
import TableContainer from '../../../common/filterTable/layout/TableContainer';
import useNewFilterTablePage from '../../../common/page/hooks/useNewFilterTablePage';
import { ExportBtn } from '../../../components/Button';
import { useDatePicker } from '../../../components/DatePicker';
import { DivideLine } from '../../../components/Layout';
import useTranslation, { Iprefix as IPrefix } from '../../../hooks/useTranslation';
import FilterSection from './components/FilterSection';
import useGetListParams from './helpers/useGetListParams';
import useTableColumns from './helpers/useTableColumns';
import useTableRows from './helpers/useTableRows';
import { FilterFields, FilterFieldsKeys } from './types/FilterFields';
import { GetAllRes } from './types/GetAllRes';
import { TableRow } from './types/Table';
import UsageDetailsDialog from './components/UsageDetailsDialog';
import UsageDetailsCell from './components/UsageDetailsCell';
import { useAlerting } from 'src/hooks';

const initFields: FilterFields = {
  [FilterFieldsKeys.CreatedDateFrom]: '',
  [FilterFieldsKeys.CreatedDateTo]: '',
  [FilterFieldsKeys.LastModifiedTimeFrom]: '',
  [FilterFieldsKeys.LastModifiedTimeTo]: '',
  [FilterFieldsKeys.ProgramName]: '',
  [FilterFieldsKeys.DistributorAgentId]: '',
  [FilterFieldsKeys.ReferralCode]: '',
  [FilterFieldsKeys.IdvMethod]: '',
  [FilterFieldsKeys.ReferralCodeStatus]: '',
  [FilterFieldsKeys.VipLevel]: '',
  [FilterFieldsKeys.ConsumedSupportedSystem]: [],
  [FilterFieldsKeys.Source]: '',
  [FilterFieldsKeys.KycLevel]: '',
};

export const translatePrefix = 'referralCode' as IPrefix;
export const permissionCode = AgentPortalFeatureCode.DistributorAgentManagement.ReferralCode;
const getTableDataApiFunc = APIs.distributorAgentManagement.referralCode.getAll;
const exportTableApiFunc = APIs.distributorAgentManagement.referralCode.getExport;

const exportBtnPermission = permissionCode.Export;

const ReferralCode = () => {
  const { getListParams } = useGetListParams();

  const DateObj = {
    CreationTime: useDatePicker(),
    LastModifiedTime: useDatePicker(),
  };

  const pageConfig = {
    initFields,
    DateObj,
    translatePrefix,
    getListParams,
    getTableDataApiFunc,
    exportTableApiFunc,
  };

  const {
    page,
    onPageChange,
    fields,
    setFields,
    list,
    onFilterSearch,
    onFilterReset,
    onExport,
    getIsHavePermission,
    Tabs,
    refreshTable,
  } = useNewFilterTablePage<FilterFields, ApiResult<GetAllRes>>(pageConfig);

  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);

  const [isUsageDetailsDialogOpen, setIsUsageDetailsDialogOpen] = useState(false);

  const { rows, totalCounts, convertToTableRows } = useTableRows({ apiList: list });
  const columns = useTableColumns({ renderUsageDetailsCell });
  const { t } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();

  const isShowExportBtn = getIsHavePermission(exportBtnPermission);

  const handleExport = async () => {
    await onExport('Referral Code', totalCounts, convertToTableRows);
  };

  function renderUsageDetailsCell({ row }: { row: TableRow }) {
    const isDisabled = !row.rawData.currentUseCount;

    const handleUsageDetailsCellClick = async () => {
      setSelectedRow(row);

      setIsUsageDetailsDialogOpen(true);
    };

    return <UsageDetailsCell onClick={handleUsageDetailsCellClick} />;
  }

  return (
    <>
      <FilterTableLayoutContainer>
        <UsageDetailsDialog
          isOpen={isUsageDetailsDialogOpen}
          closeDialog={() => setIsUsageDetailsDialogOpen(false)}
          row={selectedRow}
        />

        <FilterContainer>
          <FilterSection
            fields={fields}
            setFields={setFields}
            onSearch={onFilterSearch}
            onReset={onFilterReset}
            DateObj={DateObj}
          />
        </FilterContainer>

        <DivideLine />

        <TableContainer>
          <ExportBtn onExport={handleExport} isShow={isShowExportBtn} />
          <Tabs>
            <Table
              columns={columns}
              rows={rows}
              rowCount={totalCounts}
              page={page}
              onPageChange={onPageChange}
            />
          </Tabs>
        </TableContainer>
      </FilterTableLayoutContainer>
    </>
  );
};

export default ReferralCode;
